/* eslint-disable no-return-assign, no-restricted-syntax, guard-for-in */
import {
  IStateMetaData,
  IMachineStateRef,
  IMachineContext,
  TInputTypes,
  TMachineActions,
  TMachineEvents,
  TMachineStates,
  TRefEventsActions,
  TMachineGlobalEvents,
  IYardageSelectionEvent,
  TRefEventStates,
} from '@/types';
import { MAIN_MACHINE_ID } from '@/constants';
import { createMachine, interpret } from 'xstate';
import { getMachineActions } from './stateActions';
import { ref, computed } from 'vue';
import {
  COIN_TOSS_MACHINE,
  KICKOFF_MACHINE,
  PAT_MACHINE,
  REGULAR_PLAY_MACHINE,
  FG_MACHINE,
} from './subWorkflows';
import { PUNT_MACHINE } from './subWorkflows/punt';
import { IDLE_REF_WF } from './subWorkflows/refEventFlows/idle';
import { CONFIRM_BASIC_META, HANDLE_YARDAGE_TOGGLES_META, handleYardageToggle } from './utils';
import {
  onFormationSelected,
  onGameSelected,
  onPlayerPreviewOk,
  onRoleSelected,
  onSidesSelected,
} from './stateMachineNextActions';

const machine = createMachine(
  {
    predictableActionArguments: true,
    id: MAIN_MACHINE_ID,
    initial: TMachineStates.GAME_SELECTION,
    context: {
      inputForNextComponent: null,
      prevStateInputs: [],
      currentWorkflow: null,
      currentPlay: null,
    } as IMachineContext,
    states: {
      [TMachineStates.GAME_SELECTION]: {
        meta: {
          component: TInputTypes.GAME_SELECTOR,
          componentConfiguration: {
            title: 'Select Game',
            showLogo: false,
            showBackBtn: false,
          },
          onNext: onGameSelected,
          onBack() {},
        } as IStateMetaData,
        on: {
          [TMachineEvents.GAME_SELECTED]: {
            target: TMachineStates.PLAYER_PREVIEW,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
        },
      },

      [TMachineStates.PLAYER_PREVIEW]: {
        meta: {
          component: TInputTypes.PLAYER_PREVIEW,
          componentConfiguration: {
            title: 'Player Preview',
            showLogo: false,
            showBackBtn: true,
          },
          onNext: onPlayerPreviewOk,
          onBack({ send }) {
            send(TMachineEvents.BACK);
          },
        } as IStateMetaData,
        on: {
          [TMachineEvents.GAME_SELECTED]: {
            target: TMachineStates.SELECT_ROLE,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.RESUME_REGULAR_PLAY]: {
            target: TMachineStates.IDLE,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.RESUME_KICKOFF]: {
            target: TMachineStates.INIT_KICKOFF,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.RESUME_PAT]: {
            target: TMachineStates.PAT,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.CHOOSE_SIDES]: {
            target: TMachineStates.SELECT_SIDE,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.BACK]: {
            target: TMachineStates.GAME_SELECTION,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
          },
        },
      },

      [TMachineStates.SELECT_ROLE]: {
        meta: {
          component: TInputTypes.TWO_ROW_GENERIC,
          componentConfiguration: {
            title: 'Select Role',
            showLogo: false,
            showBackBtn: false,
          },
          onNext: onRoleSelected,
          onBack() {},
        } as IStateMetaData,
        on: {
          [TMachineEvents.ROLE_SELECTED]: {
            target: TMachineStates.INIT_COIN,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.RESUME_REGULAR_PLAY]: {
            target: TMachineStates.IDLE,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.RESUME_KICKOFF]: {
            target: TMachineStates.INIT_KICKOFF,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.RESUME_PAT]: {
            target: TMachineStates.PAT,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.CHOOSE_SIDES]: {
            target: TMachineStates.SELECT_SIDE,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
        },
      },

      [TMachineStates.SELECT_SIDE]: {
        meta: {
          component: TInputTypes.TWO_ROW_GENERIC,
          componentConfiguration: {
            title: 'Team on Left',
            showLogo: false,
            showBackBtn: true,
          },
          onNext: onSidesSelected,
          onBack({ send }) {
            send(TMachineEvents.BACK);
          },
        } as IStateMetaData,
        on: {
          [TMachineEvents.RESUME_REGULAR_PLAY]: {
            target: TMachineStates.IDLE,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.RESUME_KICKOFF]: {
            target: TMachineStates.INIT_KICKOFF,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.RESUME_PAT]: {
            target: TMachineStates.PAT,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.BACK]: {
            target: TMachineStates.GAME_SELECTION,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
          },
        },
      },

      [TMachineStates.IDLE]: {
        meta: {
          component: TInputTypes.TWO_ROW_GENERIC,
          componentConfiguration: {
            title: 'Select Formation',
            showLogo: true,
            showBackBtn: false,
          },
          onNext: onFormationSelected,
          onBack() {},
        } as IStateMetaData,
        on: {
          [TMachineEvents.REGULAR_PLAY_SELECTED]: {
            target: TMachineStates.INIT_REGULAR_PLAY,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.FIELD_GOAL_SELECTED]: {
            target: TMachineStates.INIT_FIELD_GOAL,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.KICKOFF_SELECTED]: {
            target: TMachineStates.INIT_KICKOFF,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.TO_PUNT]: {
            target: TMachineStates.INIT_PUNT,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.ON_REF_EVENT]: {
            target: TMachineStates.REF_EVENT,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
        },
      },

      [TMachineStates.INIT_COIN]: COIN_TOSS_MACHINE,

      [TMachineStates.INIT_KICKOFF]: KICKOFF_MACHINE,

      [TMachineStates.INIT_REGULAR_PLAY]: REGULAR_PLAY_MACHINE,

      [TMachineStates.PAT]: PAT_MACHINE,

      [TMachineStates.INIT_FIELD_GOAL]: FG_MACHINE,

      [TMachineStates.INIT_PUNT]: PUNT_MACHINE,

      [TMachineStates.REF_EVENT]: IDLE_REF_WF,

      [TMachineStates.YARDAGE_SELECTION]: {
        meta: {
          component: TInputTypes.YARDAGE_SELECTOR,
          componentConfiguration: {
            title: 'Post-Penalty Yardline',
            showLogo: true,
            showBackBtn: true,
          },
          onNext(inp) {
            // will only come here thru penalty so play result is set there
            const { payload } = inp;

            handleYardageToggle(inp, TMachineEvents.BACK_TO_YARDAGE_SELECTION, {
              side: (payload as IYardageSelectionEvent).side,
              yardLine: (payload as IYardageSelectionEvent).yardLine,
              currToggle: 0,
            });
          },
          onBack({ send, context }) {
            send(context.inputForNextComponent.backTo, {});
          },
        } as IStateMetaData,
        on: {
          [TMachineEvents.BACK_TO_SAME]: {
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.TO_HANDLE_YARDAGE_TOGGLE]: {
            target: TMachineStates.HANDLE_YARDAGE_TOGGLES,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.PLAY_COMPLETED]: {
            target: TMachineStates.CONFIRMATION,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TRefEventsActions.BACK_TO_PENALTY]: {
            target: `#${MAIN_MACHINE_ID}.${TMachineStates.REF_EVENT}.${TRefEventStates.PENALTY}`,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
          },
        },
      },

      [TMachineStates.HANDLE_YARDAGE_TOGGLES]: {
        meta: HANDLE_YARDAGE_TOGGLES_META,
        on: {
          [TMachineEvents.BACK_TO_YARDAGE_SELECTION]: {
            target: TMachineStates.YARDAGE_SELECTION,
            actions: [
              TMachineActions.UPDATE_CONTEX,
              TMachineActions.UNSET_NEXT_STATE_INPUT,
              TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
            ],
          },
          [TMachineEvents.BACK_TO_SAME]: {
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.TO_HANDLE_YARDAGE_TOGGLE]: {
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.PLAY_COMPLETED]: {
            target: TMachineStates.CONFIRMATION,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
        },
      },

      [TMachineStates.CONFIRMATION]: {
        meta: CONFIRM_BASIC_META,
        on: {
          [TMachineEvents.CONFIRMED]: {
            target: `#${MAIN_MACHINE_ID}.${TMachineStates.IDLE}`,
            actions: [
              TMachineActions.UPDATE_CONTEX,
              TMachineActions.SET_NEXT_STATE_INPUT,
              TMachineActions.SAVE_PLAY,
              TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
            ],
          },
          [TMachineEvents.TO_PAT]: {
            target: `#${MAIN_MACHINE_ID}.${TMachineStates.PAT}`,
            actions: [
              TMachineActions.UPDATE_CONTEX,
              TMachineActions.SET_NEXT_STATE_INPUT,
              TMachineActions.SAVE_PLAY,
              TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
            ],
          },
          [TMachineEvents.ON_REF_EVENT]: {
            target: TMachineStates.REF_EVENT,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.BACK_TO_YARDAGE_SELECTION]: {
            target: TMachineStates.YARDAGE_SELECTION,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
          },
          [TMachineEvents.BACK_TO_HANDLE_YARDAGE]: {
            target: TMachineStates.HANDLE_YARDAGE_TOGGLES,
            actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
          },
        },
      },

      [TMachineStates.GAME_OVER]: {
        meta: {
          component: TInputTypes.NONE,
          componentConfiguration: {
            title: 'Game Over',
            showLogo: false,
            showBackBtn: false,
          },
          onNext() {},
          onBack() {},
        } as IStateMetaData,
      },
    },
    on: {
      [TMachineGlobalEvents.TO_REGULAR_PLAY_GLOBAL]: {
        target: TMachineStates.INIT_REGULAR_PLAY,
        actions: [
          TMachineActions.RESET_CONTEXT,
          TMachineActions.UPDATE_CONTEX,
          TMachineActions.SET_NEXT_STATE_INPUT,
        ],
      },
      [TMachineGlobalEvents.TO_IDLE_GLOBAL]: {
        target: TMachineStates.IDLE,
        actions: [
          TMachineActions.RESET_CONTEXT,
          TMachineActions.CLEAR_PLAY,
          TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          TMachineActions.SET_NEXT_STATE_INPUT,
        ],
      },
      [TMachineGlobalEvents.TO_PAT_GLOBAL]: {
        target: TMachineStates.PAT,
        actions: [
          TMachineActions.UPDATE_CONTEX,
          TMachineActions.SET_NEXT_STATE_INPUT,
          TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
        ],
      },
      [TMachineGlobalEvents.TO_COIN_TOSS_GLOBAL]: {
        target: TMachineStates.INIT_COIN,
        actions: [
          TMachineActions.RESET_CONTEXT,
          TMachineActions.UPDATE_CONTEX,
          TMachineActions.SET_NEXT_STATE_INPUT,
        ],
      },
    },
  },
  {
    actions: {
      ...getMachineActions(),
    },
  }
);

const service = interpret(machine).start();

export function useStatTakerMachine() {
  const state = ref(service.initialState);
  const defaultMeta = { componentConfiguration: {}, component: TInputTypes.NONE };
  service.onTransition((nextState) => (state.value = nextState));

  const metadata = computed(() => {
    if (typeof state.value.value === 'object') {
      let key = Object.keys(state.value.value)[0];
      let value = (state.value.value as any)[key];
      let keyForMeta = MAIN_MACHINE_ID;

      while (typeof value === 'object') {
        keyForMeta += `.${key}`;
        [key] = Object.keys(value);
        value = value[key];
      }

      return (state.value.meta[`${keyForMeta}.${key}.${value}`] || defaultMeta) as IStateMetaData;
    }
    return (state.value.meta[`${MAIN_MACHINE_ID}.${state.value.value}`] || defaultMeta) as IStateMetaData;
  });

  const context = computed(() => state.value.context);

  return {
    state: state as IMachineStateRef,
    send: service.send as (event: string, payload: any) => null,
    metadata,
    context,
  };
}
